import React from 'react'
import { Card } from 'react-bootstrap'
import { Link } from 'react-router-dom'

import Moment from 'moment'

import AddButton from '../components/controls/AddButton'
import FAIcon from '../components/FAIcon'

import './AppointmentDashboardCard.scss'

function AppointmentDashboardCard({
  appointment,
  getValue,
  getFormattedDateTimeValue,
  convertUtcToLocalDateTime,
  handleButtonClick,
  cases = [],
  showCaseDetails = false,
  singleColumn = false,
}) {
  function getCaseInfoForAppointment(appointment, classes = '') {
    const fullName = cases.map(caseObj => {
      if (caseObj.caseNumber === appointment.caseNumber) {
        return `${getValue('client.firstName', '', caseObj)} ${getValue('client.lastName', '', caseObj)}`
      } else {
        return ''
      }
    })

    return (
      <p className={classes}>
        <strong>
          <Link to={`/cases/${appointment.caseNumber}?returnTo=/`} className="text-dark">
            {fullName}
          </Link>
        </strong>
        <br />
        <Link to={`/cases/${appointment.caseNumber}?returnTo=/`} className="text-muted">
          Case #: {appointment.caseNumber}
        </Link>
      </p>
    )
  }

  const addressLine1 = getValue('address.line1', '', appointment)
  const addressCity = getValue('address.city', '', appointment)
  const addressState = getValue('address.state', '', appointment)
  const addressZip = getValue('address.zip', '', appointment)

  const startTimeLocal = convertUtcToLocalDateTime(getValue('startUtc', null, appointment), appointment)
  const endTimeLocal = convertUtcToLocalDateTime(getValue('endUtc', null, appointment), appointment)
  const isUpcoming = (() => {
    if (!startTimeLocal || !appointment?.startUtc) return true
    if (appointment?.isAllDay) {
      const apptDateLocal = Moment(Moment.utc(appointment.startUtc).format('YYYY-MM-DD'), 'YYYY-MM-DD')
      return Moment().local().isSameOrBefore(apptDateLocal.endOf('day'))
    }
    return new Date() < new Date(startTimeLocal)
  })()
  const isComplete = Boolean(appointment?.complete)
  const cardLabel = isComplete
    ? 'COMPLETED'
    : !startTimeLocal || !appointment?.startUtc
    ? 'TO BE SCHEDULED'
    : isUpcoming
    ? 'UPCOMING'
    : 'PAST'

  const providerOrg = getValue('provider', '', appointment)
  const individualProvider = getValue('individualProvider', '', appointment)
  const providerParts = []
  if (individualProvider) providerParts.push(individualProvider)
  if (providerOrg) providerParts.push(providerOrg)
  const providerDisplayText = providerParts.join(' / ')

  const appointmentUrl = `/cases/${appointment.caseNumber}/appointments/${appointment.appointmentNumber}`

  return (
    <div
      className={`${singleColumn ? 'col-lg-12' : 'col-lg-6'} col-md-12 col-sm-12 mb-3 px-0 appointment-card-container`}
    >
      <div className="col-md-12 h-100">
        <Card className="shadow h-100">
          <Card.Body className="appointment-card-body">
            <div>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <p className={`card-label mb-0 ${isComplete ? 'text-success' : ''}`}>{cardLabel}</p>
                <div className="d-flex align-items-center appointment-card-quick-actions">
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-1 text-secondary mr-1 edit-appointment-pencil-btn"
                    title="Edit appointment"
                    aria-label="Edit appointment"
                    onClick={() => handleButtonClick(appointmentUrl)}
                  >
                    <FAIcon name="pencil-alt" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-1 text-primary duplicate-appointment-icon-btn"
                    title="Duplicate this appointment"
                    aria-label="Duplicate this appointment"
                    onClick={() =>
                      handleButtonClick(
                        `/cases/${appointment.caseNumber}/appointments?duplicateFrom=${appointment.appointmentNumber}`,
                        { duplicateAppointment: appointment },
                      )
                    }
                  >
                    <FAIcon name="copy" />
                  </button>
                </div>
              </div>
              <a className="main-link" href={appointmentUrl}>
                <p className="mb-0">
                  <strong>{getValue('title', '', appointment)}</strong>
                </p>
                {getFormattedDateTimeValue(startTimeLocal, endTimeLocal, appointment)}
              </a>
              <div>
                <hr className="mt-1 mt-2" />
                {!!showCaseDetails && (
                  <div className="d-table mb-2">
                    <FAIcon type="fas" name="poll-h" className="d-table-cell cool-grey-text align-middle" />
                    {getCaseInfoForAppointment(appointment, 'mb-0 ml-2 d-inline-block footnote')}
                  </div>
                )}
                {!!providerDisplayText && (
                  <div className="mb-1">
                    <FAIcon type="fas" name="user-md" className="d-inline-block float-left cool-grey-text" />
                    <p className="mb-0 provider-line footnote">{providerDisplayText}</p>
                  </div>
                )}
                {!!addressLine1 && !!addressCity && !!addressState && !!addressZip ? (
                  <React.Fragment>
                    <FAIcon name="fas fa-map-marker-alt" className="d-inline-block float-left cool-grey-text" />
                    <p className="mb-0 address-line footnote">{`${addressLine1} ${addressCity}, ${addressState} ${addressZip}`}</p>
                  </React.Fragment>
                ) : (
                  <AddButton className="footnote p-0" onClick={() => handleButtonClick(appointmentUrl)}>
                    Add Location
                  </AddButton>
                )}
              </div>
            </div>
            <div className="d-flex flex-wrap align-items-center mt-3 appointment-card-actions-footer">
              <button
                type="button"
                className="btn btn-secondary btn-sm mr-2 mb-1"
                style={{ whiteSpace: 'nowrap' }}
                onClick={() => handleButtonClick(appointmentUrl)}
              >
                <FAIcon name={isComplete || isUpcoming ? 'pencil-alt' : 'check'} className="mr-1" />
                {isComplete || isUpcoming ? 'Edit' : 'Complete'}
              </button>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm mb-1 duplicate-appointment-card-btn"
                style={{ whiteSpace: 'nowrap' }}
                title="Duplicate this appointment"
                onClick={() =>
                  handleButtonClick(
                    `/cases/${appointment.caseNumber}/appointments?duplicateFrom=${appointment.appointmentNumber}`,
                    { duplicateAppointment: appointment },
                  )
                }
              >
                <FAIcon name="copy" className="mr-1" /> Duplicate
              </button>
            </div>
          </Card.Body>
        </Card>
      </div>
    </div>
  )
}

export default AppointmentDashboardCard
